/** Back office → one contract: parties, checks, PDF, signature, schedule, log. */

import { type Blocker, type ContractDetail, useContractsItemsRetrieve } from "@agencyos/api-client";
import { formatXaf } from "@agencyos/core";
import { Avatar, Badge, GlassCard, Skeleton } from "@agencyos/ui-web";
import { CircleCheck, CircleX } from "lucide-react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";

import { Page } from "../components/AppShell";
import { Lifecycle } from "./contracts/Lifecycle";
import { PartiesForm } from "./contracts/PartiesForm";
import { EvidenceLog, Schedule } from "./contracts/ScheduleAndLog";
import { STATUS_TONE, dateOnly } from "./contracts/shared";
import { TermsForm } from "./contracts/TermsForm";

type Tab = "contract" | "schedule" | "log";

function Progress({ contract }: { contract: ContractDetail }) {
  const { t, i18n } = useTranslation();
  const at = (kind: string) => contract.events.find((e) => e.kind === kind)?.created_at;
  const steps = [
    { key: "draft", done: true, date: at("created") ?? contract.created_at },
    { key: "generated", done: Boolean(contract.number), date: at("generated") },
    { key: "sent", done: Boolean(at("sent")) || contract.status === "signed", date: at("sent") },
    { key: "signed", done: contract.status === "signed", date: contract.signed_at },
  ];
  const cancelled = contract.status === "cancelled";
  const current = cancelled ? -1 : steps.findIndex((s) => !s.done);
  return (
    <ol className="stepper" aria-label={t("contracts.progress.label")}>
      {steps.map((step, index) => (
        <li
          key={step.key}
          className={`stepper__step${step.done ? " stepper__step--done" : index === current ? " stepper__step--current" : ""}`}
          aria-current={index === current ? "step" : undefined}
        >
          <span className="muted">{index + 1}</span>
          {t(`contracts.progress.${step.key}`)}
          {step.done && step.date ? <span className="muted">{dateOnly(step.date, i18n.language)}</span> : null}
        </li>
      ))}
    </ol>
  );
}

function blockerText(t: (key: string, options?: Record<string, unknown>) => string, blocker: Blocker): { title: string; detail: string } {
  if (blocker.code === "missing_contract_field") {
    return {
      title: t("contracts.blockers.missing_contract_field"),
      detail: (blocker.fields ?? []).map((f) => t(`contracts.manual.field.${f}`)).join(", "),
    };
  }
  if (blocker.code === "missing_party") {
    return {
      title: t("contracts.blockers.missing_party", { role: t(`contracts.roles.${blocker.role ?? "client"}`) }),
      detail: (blocker.fields ?? []).map((f) => t(`contracts.person.${f}`)).join(", "),
    };
  }
  return {
    title: t(`contracts.blockers.${blocker.code}`, { defaultValue: blocker.message }),
    detail: [blocker.message, blocker.block ? t("contracts.blockers.inBlock", { block: blocker.block }) : ""].filter(Boolean).join(" · "),
  };
}

function Checks({ contract }: { contract: ContractDetail }) {
  const { t } = useTranslation();
  if (contract.number) return null;
  return (
    <GlassCard as="section" className="stack">
      <div className="section-title">
        <div>
          <h2>{t("contracts.checks.title")}</h2>
          <p className="muted">{t("contracts.checks.help")}</p>
        </div>
        <Badge tone={contract.blockers.length ? "danger" : "success"}>
          {contract.blockers.length ? t("contracts.checks.count", { count: contract.blockers.length }) : t("contracts.checks.ready")}
        </Badge>
      </div>
      <ul className="checks">
        {contract.blockers.length ? (
          contract.blockers.map((blocker, index) => {
            const text = blockerText(t, blocker);
            return (
              <li key={`${blocker.code}-${index}`} className="check">
                <CircleX size={18} className="check__icon--ko" aria-hidden="true" />
                <span>
                  <strong>{text.title}</strong>
                  {text.detail ? <span className="check__detail"> · {text.detail}</span> : null}
                </span>
              </li>
            );
          })
        ) : (
          <li className="check">
            <CircleCheck size={18} className="check__icon--ok" aria-hidden="true" />
            <span>
              <strong>{t("contracts.checks.allGood")}</strong>
            </span>
          </li>
        )}
      </ul>
      {contract.blockers.some((b) => b.block || b.code === "total_mismatch" || b.code === "installments_missing" || b.code === "package_not_published") ? (
        <p className="muted">
          {t("contracts.checks.fixWhere")}{" "}
          <Link className="link" to="/settings/contracts">
            {t("contracts.checks.templates")}
          </Link>{" "}
          ·{" "}
          <Link className="link" to="/catalog">
            {t("contracts.checks.catalog")}
          </Link>
        </p>
      ) : null}
    </GlassCard>
  );
}

/** Who, what, how much: the contract at a glance (aside of the detail). */
function Summary({ contract }: { contract: ContractDetail }) {
  const { t, i18n } = useTranslation();
  const signed = contract.events.find((e) => e.kind === "signed");
  return (
    <GlassCard as="section" className="stack contract-summary" aria-label={t("contracts.summary.title")}>
      <div className="contract-summary__who">
        <Avatar name={contract.contact_name} size="lg" />
        <div>
          <strong>{contract.contact_name}</strong>
          <span className="muted">{t(`contracts.kind.${contract.kind}`)}</span>
        </div>
      </div>
      <dl className="facts">
        <div>
          <dt>{t("contracts.list.package")}</dt>
          <dd>
            <span className="code-chip">{contract.package_code}</span>
          </dd>
        </div>
        <div>
          <dt>{t("contracts.list.total")}</dt>
          <dd className="ao-num facts__strong">{formatXaf(contract.total_xaf)}</dd>
        </div>
        <div>
          <dt>{t("contracts.list.created")}</dt>
          <dd className="ao-num">{dateOnly(contract.created_at, i18n.language)}</dd>
        </div>
        {contract.signed_at ? (
          <div>
            <dt>{t("contracts.progress.signed")}</dt>
            <dd className="ao-num">
              {dateOnly(contract.signed_at, i18n.language)}
              {signed?.actor_name ? <span className="muted"> · {signed.actor_name}</span> : null}
            </dd>
          </div>
        ) : null}
      </dl>
    </GlassCard>
  );
}

export function ContractPage() {
  const { t } = useTranslation();
  const { id = "" } = useParams();
  const [tab, setTab] = useState<Tab>("contract");
  const [dirty, setDirty] = useState(false);
  const onDirtyChange = useCallback((value: boolean) => setDirty(value), []);
  const contract = useContractsItemsRetrieve(id, {
    query: {
      // The numbered PDF is printed in the background: poll until its link appears.
      refetchInterval: (query) => {
        const data = query.state.data;
        if (data?.status !== 200) return false;
        const c = data.data;
        return c.number && !c.document_url && c.status !== "cancelled" && query.state.dataUpdateCount < 60 ? 2500 : false;
      },
    },
  });

  if (contract.isPending)
    return (
      <Page>
        <Skeleton style={{ height: 120 }} />
        <Skeleton style={{ height: 320 }} />
      </Page>
    );
  if (contract.data?.status !== 200)
    return (
      <Page>
        <GlassCard role="alert">{t("common.error")}</GlassCard>
      </Page>
    );
  const c = contract.data.data;
  const editable = !c.number && c.status === "draft";
  const tabs: Tab[] = c.status === "signed" && c.kind === "contract" ? ["contract", "schedule", "log"] : ["contract", "log"];
  const activeTab = tabs.includes(tab) ? tab : "contract";

  return (
    <Page
      title={
        <>
          {c.kind === "amendment" ? `${t("contracts.kind.amendment")} ` : ""}
          {c.number || t("contracts.draftNumber")}
        </>
      }
      description={
        <>
          <Link to="/contracts" className="link">
            {t("contracts.title")}
          </Link>{" "}
          · {c.contact_name} · <span className="code-chip">{c.package_code}</span> · {formatXaf(c.total_xaf)}
          {c.parent ? (
            <>
              {" "}
              ·{" "}
              <Link to={`/contracts/${c.parent}`} className="link">
                {t("contracts.amendment.parent")}
              </Link>
            </>
          ) : null}
          {c.dossier ? (
            <>
              {" "}
              ·{" "}
              <Link to={`/dossiers/${c.dossier}`} className="link">
                {t("contracts.openDossier")}
              </Link>
            </>
          ) : null}
        </>
      }
      actions={<Badge tone={STATUS_TONE[c.status]}>{t(`contracts.status.${c.status}`)}</Badge>}
    >
      <GlassCard className="stack">
        <Progress contract={c} />
      </GlassCard>
      <div className="tabs" role="tablist" aria-label={t("contracts.tabs.label")}>
        {tabs.map((key) => (
          <button
            key={key}
            type="button"
            role="tab"
            id={`tab-${key}`}
            aria-selected={activeTab === key}
            aria-controls={`panel-${key}`}
            className="tabs__tab"
            onClick={() => setTab(key)}
          >
            {t(`contracts.tabs.${key}`)}
            {key === "log" ? <Badge>{c.events.length}</Badge> : null}
          </button>
        ))}
      </div>
      <div role="tabpanel" id={`panel-${activeTab}`} aria-labelledby={`tab-${activeTab}`} className="stack">
        {activeTab === "contract" ? (
          <div className="detail-layout">
            <div className="detail-layout__main">
              <Checks contract={c} />
              <GlassCard as="section" className="stack">
                <div className="section-title">
                  <div>
                    <h2>{t("contracts.parties.title")}</h2>
                    <p className="muted">{editable ? t("contracts.parties.intro") : t("contracts.parties.frozen")}</p>
                  </div>
                </div>
                <PartiesForm key={c.id} contract={c} editable={editable} onDirtyChange={onDirtyChange} />
              </GlassCard>
              <GlassCard as="section" className="stack">
                <h2>{t("contracts.manual.termsTitle")}</h2>
                <TermsForm key={`terms-${c.id}`} contract={c} editable={editable} />
              </GlassCard>
            </div>
            <aside className="detail-layout__aside">
              <Summary contract={c} />
              <Lifecycle contract={c} dirty={dirty} />
            </aside>
          </div>
        ) : null}
        {activeTab === "schedule" ? <Schedule contract={c} /> : null}
        {activeTab === "log" ? <EvidenceLog contract={c} /> : null}
      </div>
    </Page>
  );
}
